import { ToggleButtons } from "..";
import { DRIVE_TAB } from "@/utils/constants/tabConstants";
import { useEffect, useState, useMemo } from "react";
import Video from "./Video";
import { AlertDialog } from "@/utils/ui/AlertDialog/AlertDialog";

interface ICustomUploadForm {
  closeModal: () => void;
  openUpload?: any;
  updateActiveCustomUploadTab: any;
  isPreset?: boolean;
  onNextClick?: () => void;
  isNextDisabled?: boolean;
  nextButtonText?: string;
  onAssetTypeChange?: (assetType: string) => void;
}

export const CustomUploadForm = ({
  closeModal,
  openUpload,
  updateActiveCustomUploadTab,
  isPreset,
  onNextClick,
  isNextDisabled,
  nextButtonText,
  onAssetTypeChange,
}: ICustomUploadForm) => {
  const [contentType, setContentType] = useState("video");
  const [isChangingTab, setIsChangingTab] = useState(false);
  const [isValidatingUrl, setIsValidatingUrl] = useState(false);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState<string | null>(null);

  const UploadOptions = useMemo(
    () => [
      { label: "VIDEO", value: "video" },
      { label: "AUDIO", value: "audio" },
      { label: "DRIVE", value: DRIVE_TAB },
      { label: "CAMPAIGN", value: "campaign" },
      { label: "SHORTS", value: "shorts" },
      { label: "SLATES", value: "slates" },
    ],
    []
  );

  useEffect(() => {
    updateActiveCustomUploadTab("video");
  }, []);

  const handleFormChange = (hasChanges: boolean) => {
    setHasFormChanges(hasChanges);
  };

  const handleChangeContentType = (value: string) => {
    if (isValidatingUrl) return;

    // Check if there are unsaved changes
    if (hasFormChanges && value !== contentType) {
      setPendingTabChange(value);
      setShowAlert(true);
      return;
    }

    setContentType(value);
    updateActiveCustomUploadTab(value);
    onAssetTypeChange?.(value);
  };

  const handleTabClick = () => {
    if (isValidatingUrl) return;
    setIsChangingTab(true);
  };

  // Reset isChangingTab when contentType changes
  useEffect(() => {
    if (isChangingTab) {
      setIsChangingTab(false);
    }
  }, [contentType, isChangingTab]);

  const handleConfirmTabChange = () => {
    if (pendingTabChange) {
      setContentType(pendingTabChange);
      updateActiveCustomUploadTab(pendingTabChange);
      onAssetTypeChange?.(pendingTabChange);
      setPendingTabChange(null);
      setHasFormChanges(false);
    }
    setShowAlert(false);
  };

  const handleCancelTabChange = () => {
    setShowAlert(false);
    setPendingTabChange(null);
  };

  return (
    <div style={{ width: " calc(100% + 32px)", marginLeft: "-16px" }}>
      <div className="container">
        <div className="row">
          <div className="d-flex align-items-center justify-content-center pb-3">
            <ToggleButtons
              options={UploadOptions}
              selectedValue={contentType}
              handleChange={handleChangeContentType}
              label={""}
              name={""}
              onTabClick={handleTabClick}
              disabled={isValidatingUrl}
            />
          </div>
        </div>
      </div>
      <Video
        closeModal={closeModal}
        openUpload={openUpload}
        uploadType={contentType}
        isChangingTab={isChangingTab}
        onValidatingUrlChange={setIsValidatingUrl}
        onFormChange={handleFormChange}
        isPreset={isPreset || true}
        onNextClick={onNextClick}
        isNextDisabled={isNextDisabled}
        nextButtonText={nextButtonText}
        isWizard={true}
      />

      {showAlert && (
        <AlertDialog
          isOpen={showAlert}
          onClose={handleCancelTabChange}
          onConfirm={handleConfirmTabChange}
          title="Unsaved Changes"
          message="You have unsaved changes. Are you sure you want to leave this tab?"
          confirmText="Yes, leave"
          cancelText="No, stay"
          confirmButtonType="warning"
        />
      )}
    </div>
  );
};
